import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { brandsApi } from '../../services/settingsApi';
import { useTranslation } from '../../hooks/useTranslation';

export default function BrandsTab() {
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredBrands, setFilteredBrands] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingBrand, setEditingBrand] = useState(null);
    const [formData, setFormData] = useState({
        brand_name: '',
        brand_code: '',
        country: '',
        parent_company: '',
        is_active: true
    });
    const { getMessage, getCommonLabel } = useTranslation();

    useEffect(() => {
        fetchBrands();
    }, []);

    useEffect(() => {
        let filtered = brands;

        // Filter by search query
        if (searchQuery.trim() !== '') {
            filtered = filtered.filter(brand =>
                brand.brand_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                brand.brand_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                brand.country?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                brand.parent_company?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        setFilteredBrands(filtered);
    }, [searchQuery, brands]);

    const fetchBrands = async () => {
        try {
            setLoading(true);
            console.log('Fetching brands...');
            const response = await brandsApi.getAll();
            console.log('Brands API response:', response);

            // Try different response structures
            let brandsData = [];
            if (response.data) {
                if (Array.isArray(response.data)) {
                    brandsData = response.data;
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    brandsData = response.data.data;
                } else if (response.data.brands && Array.isArray(response.data.brands)) {
                    brandsData = response.data.brands;
                }
            }

            console.log('Parsed brands data:', brandsData);
            setBrands(brandsData);

        } catch (error) {
            console.error('Error fetching brands:', error);
            toast.error(`Failed to load brands: ${error.response?.data?.message || error.message}`);
            setBrands([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleAddBrand = () => {
        setEditingBrand(null);
        setFormData({
            brand_name: '',
            brand_code: '',
            country: '',
            parent_company: '',
            is_active: true
        });
        setShowModal(true);
    };

    const handleEditBrand = (brand) => {
        setEditingBrand(brand);
        setFormData({
            brand_name: brand.brand_name || '',
            brand_code: brand.brand_code || '',
            country: brand.country || '',
            parent_company: brand.parent_company || '',
            is_active: brand.is_active !== false
        });
        setShowModal(true);
    };

    const handleDeleteBrand = async (brand) => {
        if (!window.confirm(`Are you sure you want to delete brand "${brand.brand_name}"?`)) {
            return;
        }

        try {
            await brandsApi.delete(brand.brand_id);
            toast.success('Brand deleted successfully');
            fetchBrands();
        } catch (error) {
            console.error('Error deleting brand:', error);
            toast.error('Failed to delete brand');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (editingBrand) {
                await brandsApi.update(editingBrand.brand_id, formData);
                toast.success('Brand updated successfully');
            } else {
                await brandsApi.create(formData);
                toast.success('Brand created successfully');
            }

            setShowModal(false);
            fetchBrands();
        } catch (error) {
            console.error('Error saving brand:', error);
            toast.error(error.response?.data?.error || 'Failed to save brand');
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingBrand(null);
    };

    const countries = [
        'Japan',
        'Germany',
        'USA',
        'South Korea',
        'China',
        'Italy',
        'France',
        'UK',
        'Sweden',
        'Laos',
        'Thailand',
        'Other'
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        🚗 {getMessage('settings.brands') || 'Car Brands'}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {getMessage('settings.brands_desc') || 'Manage car brands and manufacturers'}
                    </p>
                </div>
                <div className="flex space-x-3">
                    <button
                        onClick={fetchBrands}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                    >
                        🔄 {getMessage('common.refresh') || 'Refresh'}
                    </button>
                    <button
                        onClick={handleAddBrand}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                    >
                        ➕ {getCommonLabel('add') || 'Add Brand'}
                    </button>
                </div>
            </div>

            {/* Search */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="relative flex-1 max-w-md">
                    <input
                        type="text"
                        placeholder={getMessage('common.search') || 'Search brands...'}
                        value={searchQuery}
                        onChange={handleSearch}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                        🔍
                    </div>
                </div>

                {/* Summary Stats */}
                <div className="text-sm text-gray-600 dark:text-gray-400">
                    Showing {filteredBrands.length} brand(s)
                </div>
            </div>

            {/* Brands Table */}
            <div className="bg-white dark:bg-gray-700 rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                            <span className="ml-3 text-gray-600 dark:text-gray-400">Loading...</span>
                        </div>
                    ) : filteredBrands.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="text-gray-400 dark:text-gray-500 text-lg mb-2">🚗</div>
                            <p className="text-gray-600 dark:text-gray-400">
                                {searchQuery ? 'No brands found matching your search' : 'No brands available'}
                            </p>
                        </div>
                    ) : (
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Brand
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Code
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Country
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Parent Company
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
                                {filteredBrands.map((brand) => (
                                    <tr key={brand.brand_id} className="hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-150">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-gray-100 dark:bg-gray-600 rounded-full">
                                                    <span className="text-2xl">🚗</span>
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-bold text-blue-600 dark:text-blue-400">
                                                        {brand.brand_name}
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                        ID: #{brand.brand_id}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 dark:text-white font-mono">
                                                {brand.brand_code || '-'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 dark:text-white">
                                                {brand.country || '-'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">
                                                {brand.parent_company || '-'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${brand.is_active
                                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                                                }`}>
                                                <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${brand.is_active ? 'bg-green-500' : 'bg-red-500'
                                                    }`}></span>
                                                {brand.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => handleEditBrand(brand)}
                                                className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300 mr-3"
                                                title="Edit"
                                            >
                                                ✏️ Edit
                                            </button>
                                            <button
                                                onClick={() => handleDeleteBrand(brand)}
                                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                                title="Delete"
                                            >
                                                🗑️ Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {editingBrand ? 'Edit Brand' : 'Add New Brand'}
                            </h3>
                            <button
                                onClick={closeModal}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                ✕
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Brand Name *
                                </label>
                                <input
                                    type="text"
                                    name="brand_name"
                                    value={formData.brand_name}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="e.g., Toyota, BMW, Mercedes"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Brand Code
                                </label>
                                <input
                                    type="text"
                                    name="brand_code"
                                    value={formData.brand_code}
                                    onChange={handleInputChange}
                                    placeholder="e.g., TOY, BMW, MBZ"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Country
                                </label>
                                <select
                                    name="country"
                                    value={formData.country}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">Select country</option>
                                    {countries.map(country => (
                                        <option key={country} value={country}>{country}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Parent Company
                                </label>
                                <input
                                    type="text"
                                    name="parent_company"
                                    value={formData.parent_company}
                                    onChange={handleInputChange}
                                    placeholder="e.g., Toyota Motor Corporation"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="is_active"
                                    id="is_active"
                                    checked={formData.is_active}
                                    onChange={handleInputChange}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                    Active
                                </label>
                            </div>

                            <div className="flex space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                                >
                                    {editingBrand ? 'Update' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
